import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useStudent } from "@/lib/auth";

const LINKS = [
  { to: "/why-australia", label: "Why Australia" },
  { to: "/universities", label: "Universities" },
  { to: "/scholarships", label: "Scholarships" },
  { to: "/guides", label: "Guides" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { student, signInWithGoogle } = useStudent();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const onDarkHero = !scrolled && !open;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open
          ? "bg-cream/85 backdrop-blur-xl border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
            <span className={`font-serif text-3xl font-medium tracking-tight transition-colors duration-500 ${onDarkHero ? "text-cream" : "text-navy"}`}>
              Vitaliah
            </span>
            <span className="hidden sm:inline-block h-1.5 w-1.5 rounded-full bg-gold translate-y-[-2px]" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9" data-testid="nav-links">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-gold ${
                  onDarkHero ? "text-cream/85" : "text-navy/80"
                } ${location.pathname === l.to ? "text-gold" : ""}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            {student ? (
              <Link
                to="/profile"
                data-testid="nav-profile-link"
                className={`hidden sm:inline-flex items-center gap-2 text-sm font-medium transition-colors duration-300 hover:text-gold ${onDarkHero ? "text-cream/85" : "text-navy/80"}`}
              >
                {student.picture ? (
                  <img src={student.picture} alt="" className="h-7 w-7 rounded-full border border-gold object-cover" />
                ) : (
                  <User className="h-4 w-4" />
                )}
                {student.name?.split(" ")[0] || "Profile"}
              </Link>
            ) : (
              <button
                data-testid="nav-signin-button"
                onClick={signInWithGoogle}
                className={`hidden sm:inline-flex items-center gap-2 text-sm font-medium transition-colors duration-300 hover:text-gold ${onDarkHero ? "text-cream/85" : "text-navy/80"}`}
              >
                <User className="h-4 w-4" /> Sign In
              </button>
            )}
            <button
              data-testid="nav-cta-guidance"
              onClick={() => navigate("/contact")}
              className="hidden sm:inline-flex items-center gap-2 bg-navy text-cream px-6 py-2.5 text-sm font-semibold tracking-wide border border-navy hover:bg-gold hover:border-gold hover:text-navy transition-colors duration-300"
            >
              Get Free Guidance
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className={`lg:hidden p-2 transition-colors ${onDarkHero ? "text-cream" : "text-navy"}`}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-border"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-serif text-3xl text-navy hover:text-gold transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              {student ? (
                <Link to="/profile" data-testid="nav-mobile-profile" className="font-serif text-3xl text-navy hover:text-gold transition-colors">
                  My Profile
                </Link>
              ) : (
                <button data-testid="nav-mobile-signin" onClick={signInWithGoogle} className="text-left font-serif text-3xl text-navy hover:text-gold transition-colors">
                  Sign In
                </button>
              )}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => navigate("/contact")}
                className="mt-4 inline-flex items-center justify-center gap-2 bg-navy text-cream px-6 py-4 text-sm font-semibold tracking-wide"
              >
                Get Free Guidance <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
